import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiChevronDown } from 'react-icons/fi';
import type { DocCatalogEntry, TocNode } from '../../services/docsService';

interface Props {
  group: string;
  docs: DocCatalogEntry[];
  initialCollapsed: boolean;
  recentLimit: number | null;
  currentSlug: string;
  activeAnchor: string | null;     // NEW
  tocDepth: number;                // NEW (1=H2, 2=H2-H3, 3=H2-H4, Infinity=all)
  autoExpand?: boolean;
  onLinkClick?: () => void;
  darkMode?: boolean;
}

function renderTocTree(
  nodes: TocNode[],
  slug: string,
  activeAnchor: string | null,
  depthRemaining: number,
  darkMode: boolean,
  onLinkClick?: () => void,
): React.ReactElement | null {
  if (depthRemaining <= 0 || nodes.length === 0) return null;
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  return (
    <ul className={`ml-4 border-l ${borderColor}`}>
      {nodes.map((node) => {
        const isActive = activeAnchor === node.anchor;
        const activeClass = isActive
          ? darkMode
            ? 'toc-active text-emerald-400 border-l-2 border-emerald-400 -ml-px'
            : 'toc-active text-emerald-700 border-l-2 border-emerald-600 -ml-px'
          : darkMode
            ? 'text-gray-400 hover:text-gray-200'
            : 'text-gray-600 hover:text-gray-800';
        return (
          <li key={node.anchor}>
            <Link
              to={`/docs/${slug}#${node.anchor}`}
              data-testid="toc-link"
              className={`block text-xs py-0.5 pl-2 ${activeClass}`}
              onClick={onLinkClick}
            >
              {node.title}
            </Link>
            {renderTocTree(node.children, slug, activeAnchor, depthRemaining - 1, darkMode, onLinkClick)}
          </li>
        );
      })}
    </ul>
  );
}

export const DocsGroupSection: React.FC<Props> = ({
  group, docs, initialCollapsed, recentLimit, currentSlug, activeAnchor, tocDepth,
  autoExpand, onLinkClick, darkMode = true,
}) => {
  const [open, setOpen] = useState(autoExpand ? true : !initialCollapsed);
  const [showAll, setShowAll] = useState(autoExpand ?? false);

  useEffect(() => {
    if (autoExpand) {
      setOpen(true);
      setShowAll(true);
    }
  }, [autoExpand]);

  const visible = (recentLimit !== null && !showAll) ? docs.slice(0, recentLimit) : docs;
  const hiddenCount = docs.length - visible.length;

  return (
    <div className="mb-4">
      <button
        data-testid="docs-group-header"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 w-full text-left text-xs uppercase tracking-wider font-semibold transition-colors ${
          darkMode
            ? 'text-gray-500 hover:text-gray-300'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {open ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
        {group}
      </button>
      {open && (
        <ul data-testid={`docs-group-${group}`} className="mt-1 space-y-0.5">
          {visible.map((d) => {
            const active = d.slug === currentSlug;
            return (
              <li key={d.slug}>
                <Link
                  to={`/docs/${d.slug}`}
                  data-testid={active ? 'docs-link-active' : 'docs-link'}
                  className={`block px-3 py-1 text-sm rounded ${
                    active
                      ? darkMode
                        ? 'bg-emerald-900/30 text-emerald-400'
                        : 'bg-emerald-50 text-emerald-700'
                      : darkMode
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={d.summary || undefined}
                  onClick={onLinkClick}
                >
                  {d.title}
                </Link>
                {active && d.toc && d.toc.length > 0 &&
                  renderTocTree(d.toc, d.slug, activeAnchor, tocDepth, darkMode, onLinkClick)}
              </li>
            );
          })}
          {hiddenCount > 0 && (
            <li>
              <button
                onClick={() => setShowAll(true)}
                className={`block px-3 py-1 text-xs ${
                  darkMode
                    ? 'text-gray-500 hover:text-gray-300'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Show all {docs.length} →
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
